import { Box, Flex, Text, Tooltip, TextProps } from "@chakra-ui/react";
import {
  BADGE_CONFIGS,
  getBadgeClassName,
  getBadgeColors,
  getProgressBarColors,
} from "./config";
import { BadgeWithTooltip } from "@svan-garaaz/reactsharedlib";

type SupplierBadgeProps = {
  type: "coverage" | "fulfilled" | "quantity";
  value: number;
  totalHits?: number;
  variant?: "default" | "pinned";
};

interface SupplierTextProps extends Omit<TextProps, "variant"> {
  textStyle?: "header" | "title" | "subtitle" | "label";
  style?: React.CSSProperties;
  tooltip?: string;
}

interface TextStyles {
  fontSize?: string;
  fontWeight?: string | number;
  color?: string;
}

export const SupplierText = ({
  textStyle = "title",
  children,
  style,
  tooltip,
}: SupplierTextProps) => {
  const getTextStyles = (textStyle: string): TextStyles => {
    switch (textStyle) {
      case "header":
        return {
          fontSize: "lg",
          fontWeight: "bold",
          color: "black",
        };
      case "title":
        return {
          fontSize: "0.95rem",
          fontWeight: "900",
          color: "black",
        };
      case "subtitle":
        return {
          fontSize: "0.75rem",
          fontWeight: "700",
          color: "gray.500",
        };
      case "label":
        return {
          fontSize: "0.95rem",
          fontWeight: "700",
          color: "gray.600",
        };
      default:
        return {};
    }
  };

  const textContent = (
    <Text {...getTextStyles(textStyle)} style={style}>
      {children}
    </Text>
  );

  if (tooltip) {
    return (
      <Tooltip
        label={tooltip}
        hasArrow
        bg="gray.700"
        color="white"
        fontSize="xs"
      >
        {textContent}
      </Tooltip>
    );
  }

  return textContent;
};

export const SupplierProgressBadge = ({
  type,
  value,
  totalHits = 0,
  variant = "default",
}: SupplierBadgeProps) => {
  const config = BADGE_CONFIGS[type];
  const colors = getProgressBarColors(value);
  const displayValue =
    type === "coverage" && value === Infinity ? totalHits : value;

  const renderLabel = () => {
    if (variant === "pinned") {
      return (
        <Flex justify="space-between" align="center" w="100%" mb={1}>
          <Text fontSize="xs" fontWeight="bold" color={"gray.600"}>
            {config.label(0).replace("0% ", "")}
          </Text>
          <Text fontSize="xs" fontWeight="bold" color={colors.color}>
            {displayValue}%
          </Text>
        </Flex>
      );
    }
    return (
      <Text fontSize="sm" fontWeight="extrabold" color={colors.color}>
        {displayValue}%
      </Text>
    );
  };

  return (
    <Tooltip
      label={config.tooltip}
      hasArrow
      bg="gray.700"
      color="white"
      fontSize="xs"
    >
      <Flex
        direction="column"
        align={variant === "pinned" ? "flex-start" : "center"}
        w={variant === "pinned" ? "100%" : "70%"}
        minW={variant === "pinned" ? "120px" : "80px"}
        mx={variant === "pinned" ? "0" : "auto"}
      >
        {renderLabel()}
        <Box w="100%" bg="gray.200" borderRadius="full" h="1.5">
          <Box
            h="1.5"
            borderRadius="full"
            bg={colors.bg}
            width={`${Math.min(displayValue, 100)}%`}
            transition="width 0.3s ease"
          />
        </Box>
      </Flex>
    </Tooltip>
  );
};

export const SupplierBadge = ({
  type,
  value,
  totalHits = 0,
}: SupplierBadgeProps) => {
  const config = BADGE_CONFIGS[type];
  const colors = getBadgeColors(value);
  const displayValue =
    type === "coverage" && value === Infinity ? totalHits : value;

  return (
    <BadgeWithTooltip
      label={config.label(displayValue)}
      tooltip={config.tooltip}
      className={{
        ...getBadgeClassName(),
        ...colors,
      }}
    />
  );
};
